// src/services/driverService.js
import { 
  db, 
  collection, 
  getDocs, 
  doc, 
  getDoc,
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where
} from './firebase';
import { API_ENDPOINTS, PROGRAM_INFO } from '../utils/constants';

// ===== DRIVER MANAGEMENT (Firestore) =====

// Get all drivers from Firestore
export const getAllDrivers = async () => {
  try {
    const driversRef = collection(db, 'drivers');
    const snapshot = await getDocs(driversRef);
    const drivers = [];
    snapshot.forEach(doc => {
      drivers.push({
        id: doc.id,
        ...doc.data()
      });
    });
    return drivers;
  } catch (error) {
    console.error('Error fetching drivers:', error);
    throw error;
  }
};

// Get single driver by ID
export const getDriver = async (driverId) => {
  try {
    const driverRef = doc(db, 'drivers', driverId);
    const snapshot = await getDoc(driverRef);
    if (snapshot.exists()) {
      return {
        id: snapshot.id,
        ...snapshot.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching driver:', error);
    throw error;
  }
};

// Add new driver to Firestore
export const addDriver = async (driverData) => {
  try {
    const driversRef = collection(db, 'drivers');
    const docRef = await addDoc(driversRef, {
      ...driverData,
      createdAt: new Date(),
      status: driverData.status || 'active'
    });
    return {
      id: docRef.id,
      ...driverData
    };
  } catch (error) {
    console.error('Error adding driver:', error);
    throw error;
  }
};

// Update driver info
export const updateDriver = async (driverId, driverData) => {
  try {
    const driverRef = doc(db, 'drivers', driverId);
    await updateDoc(driverRef, driverData);
    return {
      id: driverId,
      ...driverData
    };
  } catch (error) {
    console.error('Error updating driver:', error);
    throw error;
  }
};

// Delete driver
export const deleteDriver = async (driverId) => {
  try {
    const driverRef = doc(db, 'drivers', driverId);
    await deleteDoc(driverRef);
  } catch (error) {
    console.error('Error deleting driver:', error);
    throw error;
  }
};

// Update driver status (active, leave, backup)
export const updateDriverStatus = async (driverId, driverName, statusType, reason = null) => {
  try {
    // Update Firestore if this is active/leave/backup status
    if (['active', 'leave', 'backup'].includes(statusType)) {
      const driverRef = doc(db, 'drivers', driverId);
      await updateDoc(driverRef, { status: statusType });
    }

    // Call Cloud Function for real-time notifications (on-way, alert)
    const response = await fetch(API_ENDPOINTS.UPDATE_DRIVER_STATUS, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        driverId,
        driverName,
        statusType,
        reason
      })
    });

    if (!response.ok) {
      throw new Error('Failed to update driver status');
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating driver status:', error);
    throw new Error('Failed to update driver status');
  }
};

// Get drivers by status
export const getDriversByStatus = async (status) => {
  try {
    const driversRef = collection(db, 'drivers');
    const q = query(driversRef, where('status', '==', status));
    const snapshot = await getDocs(q);
    const drivers = [];
    snapshot.forEach(doc => {
      drivers.push({
        id: doc.id,
        ...doc.data()
      });
    });
    return drivers;
  } catch (error) {
    console.error('Error fetching drivers by status:', error);
    throw error;
  }
};

// ===== DRIVER HELPERS (Already Exist) =====

// Send driver alert
export const sendDriverAlert = async (driverId, driverName, reason) => {
  return updateDriverStatus(driverId, driverName, 'alert', reason);
};

// Mark driver as on the way
export const markDriverOnWay = async (driverId, driverName) => {
  return updateDriverStatus(driverId, driverName, 'on-way');
};

// Build route data from students
export const buildRoute = (students) => {
  const route = { schools: [] };
  
  students.forEach(student => {
    let school = route.schools.find(s => s.name === student.schoolName);
    
    if (!school) {
      school = {
        id: `school_${student.schoolName}`,
        name: student.schoolName,
        address: student.schoolAddress,
        phone: student.schoolPhone,
        dismissalTime: {
          normal: student.dismissalTimeNormal || '3:15 PM',
          wednesday: student.dismissalTimeWednesday || '2:15 PM'
        },
        students: []
      };
      route.schools.push(school);
    }
    
    school.students.push({
      id: student.id,
      firstName: student.firstName,
      lastName: student.lastName,
      grade: student.grade,
      parentName: student.parentName,
      parentPhone: student.parentPhone,
      parentEmail: student.parentEmail,
      dropoffAddress: PROGRAM_INFO.address,
      medicalConditions: student.medicalConditions || 'None',
      authorizedPickup: student.authorizedPickup ? 
        student.authorizedPickup.split(',').map(p => p.trim()) : [],
      status: student.status || 'pending',
      pickupTime: student.pickupTime,
      deliveryTime: student.deliveryTime,
      programSelection: student.programSelection || 'Unknown'
    });
  });
  
  return route;
};

// Get route statistics
export const getRouteStats = (route) => {
  const totalStudents = route.schools.reduce((acc, school) => 
    acc + school.students.length, 0);
  
  const pending = route.schools.reduce((acc, school) => 
    acc + school.students.filter(s => s.status === 'pending').length, 0);
  
  const pickedUp = route.schools.reduce((acc, school) => 
    acc + school.students.filter(s => s.status === 'picked-up').length, 0);
  
  const delivered = route.schools.reduce((acc, school) => 
    acc + school.students.filter(s => s.status === 'delivered').length, 0);
  
  return {
    totalStudents,
    pending,
    pickedUp,
    delivered,
    percentComplete: totalStudents > 0 ? 
      Math.round((delivered / totalStudents) * 100) : 0
  };
};